import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Slide,
	Grow,
	Typography,
	IconButton,
	Button,
	TextField,
	MenuItem,
	Grid,
	CircularProgress
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CloseIcon from '@material-ui/icons/Close';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from 'clsx';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../../Services/API_Service';
import { req_promo_campaign, req_promotion_type, req_stock_brand } from '../../../Helper/Constants';
import { validatePublish } from '../helpers';
import SkuSection from './SkuSection';
import OutletSection from './OutletSection';
import TaskSection from './TaskSection';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
	return <Grow ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		scrollbarWidth: 'thin',
		scrollbarColor: `${fade(theme.palette.text.primary, 0.28)} transparent`,
		'&::-webkit-scrollbar, & ::-webkit-scrollbar': {
			width: 10,
			height: 10,
			backgroundColor: 'transparent'
		},
		'&::-webkit-scrollbar-thumb, & ::-webkit-scrollbar-thumb': {
			backgroundColor: fade(theme.palette.text.primary, 0.24),
			borderRadius: 8,
			border: '2px solid transparent',
			backgroundClip: 'padding-box'
		},
		'&::-webkit-scrollbar-thumb:hover, & ::-webkit-scrollbar-thumb:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.4)
		},
		'&::-webkit-scrollbar-corner, & ::-webkit-scrollbar-corner': {
			backgroundColor: 'transparent'
		}
	},
	header: {
		position: 'sticky',
		top: 0,
		zIndex: 10,
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper
	},
	headerTitle: {
		fontWeight: 700,
		letterSpacing: '-0.01em'
	},
	headerSpacer: {
		flex: 1
	},
	statusPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		height: 22,
		paddingLeft: theme.spacing(0.75),
		paddingRight: theme.spacing(1),
		borderRadius: 11,
		fontSize: 11.5,
		fontWeight: 700
	},
	statusDot: {
		fontSize: 8
	},
	pillDraft: {
		color: theme.palette.type === 'dark' ? '#ffcf7d' : '#8a5b00',
		border: `1px dashed ${fade('#c98b00', 0.6)}`
	},
	pillPublished: {
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43',
		backgroundColor: fade(theme.palette.success.main, theme.palette.type === 'dark' ? 0.18 : 0.14)
	},
	discardBtn: {
		color: theme.palette.error.main,
		fontWeight: 600,
		textTransform: 'none'
	},
	draftBtn: {
		fontWeight: 700,
		textTransform: 'none',
		borderRadius: 10
	},
	publishBtn: {
		backgroundColor: '#FEF200',
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		textTransform: 'none',
		borderRadius: 10,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], { duration: 180 }),
		'&:hover': {
			backgroundColor: '#FFF53D',
			transform: 'translateY(-1px)',
			boxShadow: `0 6px 16px ${fade('#FEF200', 0.5)}`
		},
		'&:active': {
			transform: 'translateY(0)'
		}
	},
	body: {
		backgroundColor: theme.palette.type === 'dark'
			? theme.palette.background.default
			: fade(theme.palette.text.primary, 0.02),
		minHeight: '100%',
		padding: theme.spacing(1.5, 1.5, 5)
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5)
	},
	'@keyframes sectionIn': {
		from: { opacity: 0, transform: 'translateY(10px)' },
		to: { opacity: 1, transform: 'none' }
	},
	sectionCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper,
		overflow: 'hidden',
		animation: '$sectionIn 320ms cubic-bezier(0.16, 1, 0.3, 1) both'
	},
	s0: { animationDelay: '0ms' },
	s1: { animationDelay: '60ms' },
	s2: { animationDelay: '120ms' },
	s3: { animationDelay: '180ms' },
	sectionHead: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
		padding: theme.spacing(1.25, 2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	sectionTitle: {
		fontSize: 13,
		fontWeight: 700,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		marginRight: 'auto'
	},
	sectionBody: {
		padding: theme.spacing(2)
	}
}));

const emptyForm = () => ({
	id: null,
	name: '',
	brand_id: '',
	brand_name: '',
	promotion_type_id: '',
	type_name: '',
	start_date: '',
	end_date: '',
	task_start_date: '',
	task_due_date: '',
	publish_status: 0,
	skus: [],
	outlets: [],
	tasks: []
});

const mapDetailToForm = (extraData) => {
	const c = extraData.campaign || {};
	return {
		id: parseInt(c.id),
		name: c.name || '',
		brand_id: parseInt(c.brand_id) > 0 ? String(c.brand_id) : '',
		brand_name: c.brand_name || '',
		promotion_type_id: c.promotion_type_id ? String(c.promotion_type_id) : '',
		type_name: c.type_name || '',
		start_date: c.start_date || '',
		end_date: c.end_date || '',
		task_start_date: c.task_start_date || '',
		task_due_date: c.task_due_date || '',
		publish_status: parseInt(c.publish_status) === 1 ? 1 : 0,
		skus: (extraData.skus || []).map((s) => ({
			id: s.id,
			product_id: s.product_id,
			product_code: s.product_code || '',
			product_name: s.product_name || '',
			normal_price: s.normal_price === null || s.normal_price === undefined ? '' : String(s.normal_price),
			promo_price: s.promo_price === null || s.promo_price === undefined ? '' : String(s.promo_price)
		})),
		outlets: (extraData.outlets || []).map((o) => ({
			id: o.id,
			cust_code: o.cust_code || '',
			cust_company_name: o.cust_company_name || o.cust_code || '',
			cust_chain_id: o.cust_chain_id ? String(o.cust_chain_id) : '0',
			cust_chain_name: o.cust_chain_name || ''
		})),
		tasks: (extraData.tasks || []).map((t) => ({
			id: t.id,
			description: t.description || '',
			input_type: t.input_type || 'checkbox',
			applies_to: t.applies_to || 'all',
			ref_photo_url: t.ref_photo_url || '',
			ref_photo_base64: '',
			is_required: parseInt(t.is_required) === 1,
			task_skus: Array.isArray(t.task_skus) ? t.task_skus.map(String) : []
		}))
	};
};

export default function PromoFormDialog({ open, campaignId, client, language, onSnack, onClose }) {
	const classes = useStyles();

	const [form, setForm] = useState(emptyForm());
	const [dirty, setDirty] = useState(false);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [saving, setSaving] = useState(false);
	const [brands, setBrands] = useState([]);
	const [types, setTypes] = useState([]);
	const [errors, setErrors] = useState([]);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [pendingBrand, setPendingBrand] = useState(null);
	const sectionRefs = useRef({});

	useEffect(() => {
		if (!open) {
			return;
		}
		setForm(emptyForm());
		setDirty(false);
		setErrors([]);
		setConfirmDiscard(false);
		setPendingBrand(null);
		loadBrands();
		loadTypes();
		if (campaignId) {
			loadDetail(campaignId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, campaignId]);

	const loadBrands = () => {
		API_Service.get(req_stock_brand, axiosDefaultConfig(client))
			.then(({ data }) => {
				const active = Array.isArray(data) ? data.filter((b) => parseInt(b.brand_status) === 1) : [];
				setBrands(active);
			})
			.catch(() => onSnack(language.no_internet || 'Network error', 'error'));
	};

	const loadTypes = () => {
		API_Service.get(req_promotion_type, axiosDefaultConfig(client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (parseInt(result) === 1 && Array.isArray(extraData)) {
					setTypes(extraData.filter((t) => parseInt(t.status) === 1));
				} else {
					setTypes([]);
				}
			})
			.catch(() => onSnack(language.no_internet || 'Network error', 'error'));
	};

	const loadDetail = (id) => {
		setLoadingDetail(true);
		API_Service.get(`${req_promo_campaign}?id=${id}`, axiosDefaultConfig(client))
			.then(({ data }) => {
				const { result, extraData, message } = data;
				if (parseInt(result) === 1) {
					setForm(mapDetailToForm(extraData));
				} else {
					onSnack(message || language.no_data, 'error');
					onClose(false);
				}
			})
			.catch(() => {
				onSnack(language.no_internet || 'Network error', 'error');
				onClose(false);
			})
			.finally(() => setLoadingDetail(false));
	};

	const onFormChange = (updater) => {
		setDirty(true);
		setForm(updater);
	};

	const updateField = (key) => (e) => {
		const value = e.target.value;
		onFormChange((prev) => ({ ...prev, [key]: value }));
	};

	const onBrandSelect = (e) => {
		const value = e.target.value;
		if (form.skus.length > 0 && value !== form.brand_id) {
			setPendingBrand(value);
			return;
		}
		onFormChange((prev) => ({ ...prev, brand_id: value }));
	};

	const confirmBrandChange = () => {
		const value = pendingBrand;
		setPendingBrand(null);
		onFormChange((prev) => ({
			...prev,
			brand_id: value,
			skus: [],
			tasks: prev.tasks.map((t) => ({ ...t, task_skus: [] }))
		}));
	};

	const askClose = () => {
		if (saving) {
			return;
		}
		if (dirty) {
			setConfirmDiscard(true);
			return;
		}
		onClose(false);
	};

	const fieldError = (field) => {
		const found = errors.find((err) => err.field === field);
		return found ? found.message : '';
	};

	const buildPayload = (publishStatus) => ({
		name: form.name.trim(),
		brand_id: form.brand_id || '',
		promotion_type_id: form.promotion_type_id || '',
		start_date: form.start_date || '',
		end_date: form.end_date || '',
		task_start_date: form.task_start_date || '',
		task_due_date: form.task_due_date || '',
		publish_status: publishStatus,
		skus: JSON.stringify(form.skus.map((s, i) => ({
			id: s.id || '',
			product_id: s.product_id,
			product_code: s.product_code,
			normal_price: s.normal_price,
			promo_price: s.promo_price,
			sequence_no: i
		}))),
		outlets: JSON.stringify(form.outlets.map((o) => ({
			cust_code: o.cust_code,
			cust_chain_id: o.cust_chain_id
		}))),
		tasks: JSON.stringify(form.tasks.map((t, i) => ({
			id: t.id || '',
			description: t.description,
			input_type: t.input_type,
			applies_to: t.applies_to,
			ref_photo_url: t.ref_photo_url || '',
			ref_photo_base64: t.ref_photo_base64 || '',
			is_required: t.is_required ? 1 : 0,
			sequence_no: i,
			task_skus: t.task_skus
		})))
	});

	const doSave = (publishStatus) => {
		if (!form.name.trim()) {
			setErrors([{ section: 1, field: 'name', message: language.err_name_required }]);
			onSnack(language.err_name_required, 'error');
			scrollToSection(1);
			return;
		}
		if (publishStatus === 1) {
			const found = validatePublish(form, language);
			setErrors(found);
			if (found.length > 0) {
				onSnack(found[0].message, 'error');
				scrollToSection(found[0].section);
				return;
			}
		} else {
			setErrors([]);
		}
		setSaving(true);
		const payload = buildPayload(publishStatus);
		const request = form.id
			? API_Service.put(req_promo_campaign, axiosURIencode({ ...payload, id: form.id }), axiosPutConfig(client))
			: (() => {
				const formdata = new FormData();
				Object.keys(payload).forEach((key) => formdata.append(key, payload[key]));
				return API_Service.post(req_promo_campaign, formdata, axiosDefaultConfig(client));
			})();
		request
			.then(({ data }) => {
				const { result, message } = data;
				if (parseInt(result) === 1) {
					onSnack(publishStatus === 1 ? language.publish_success : language.draft_saved, 'success');
					onClose(true);
				} else {
					onSnack(message || language.update_fail, 'error');
				}
			})
			.catch(() => onSnack(language.no_internet || 'Network error', 'error'))
			.finally(() => setSaving(false));
	};

	const scrollToSection = (section) => {
		const el = sectionRefs.current[section];
		if (el && el.scrollIntoView) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const assignSectionRef = (section) => (el) => {
		sectionRefs.current[section] = el;
	};

	const published = form.publish_status === 1;

	const renderDetailSkeleton = () => (
		<div className={classes.sectionBody}>
			<Skeleton animation="wave" variant="text" width="40%" height={28} />
			<Skeleton animation="wave" variant="text" width="70%" height={20} />
			<Skeleton animation="wave" variant="rect" width="100%" height={120} />
		</div>
	);

	return (
		<Dialog fullScreen open={open} onClose={askClose} TransitionComponent={SlideUp} classes={{ paper: classes.dialogPaper }}>
			<div className={classes.header}>
				<IconButton edge="start" onClick={askClose} disabled={saving}>
					<CloseIcon />
				</IconButton>
				<Typography variant="h6" className={classes.headerTitle}>
					{form.id ? language.edit_promotion : language.new_promotion}
				</Typography>
				<span className={clsx(classes.statusPill, published ? classes.pillPublished : classes.pillDraft)}>
					<FiberManualRecordIcon className={classes.statusDot} />
					{published ? language.published : language.draft}
				</span>
				<div className={classes.headerSpacer} />
				<Button className={classes.discardBtn} onClick={askClose} disabled={saving}>
					{language.discard}
				</Button>
				<Button
					variant="outlined"
					className={classes.draftBtn}
					onClick={() => doSave(0)}
					disabled={saving || loadingDetail}
					startIcon={saving ? <CircularProgress size={14} /> : null}>
					{language.save_draft}
				</Button>
				<Button
					variant="contained"
					disableElevation
					className={classes.publishBtn}
					onClick={() => doSave(1)}
					disabled={saving || loadingDetail}
					startIcon={saving ? <CircularProgress size={14} /> : null}>
					{language.publish}
				</Button>
			</div>

			<div className={classes.body}>
				<div className={classes.container}>
					<div className={clsx(classes.sectionCard, classes.s0)} ref={assignSectionRef(1)}>
						<div className={classes.sectionHead}>
							<Typography className={classes.sectionTitle}>{language.promotion_details}</Typography>
						</div>
						{loadingDetail ? renderDetailSkeleton() : (
							<div className={classes.sectionBody}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<TextField
											fullWidth
											required
											variant="outlined"
											size="small"
											label={language.promotion_name || language.new_promotion}
											value={form.name}
											error={!!fieldError('name')}
											helperText={fieldError('name')}
											onChange={updateField('name')}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<TextField
											select
											fullWidth
											required
											variant="outlined"
											size="small"
											label={language.brand || 'Brand'}
											value={form.brand_id}
											error={!!fieldError('brand_id')}
											helperText={fieldError('brand_id')}
											onChange={onBrandSelect}>
											{form.brand_id
												&& !brands.some((b) => String(b.brand_id) === form.brand_id) ? (
													<MenuItem value={form.brand_id} disabled>{form.brand_name || `#${form.brand_id}`}</MenuItem>
												) : null}
											{brands.map((b) => (
												<MenuItem key={b.brand_id} value={String(b.brand_id)}>{b.brand_name}</MenuItem>
											))}
										</TextField>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<TextField
											select
											fullWidth
											variant="outlined"
											size="small"
											label={language.promotion_type_name || 'Promotion Type'}
											value={form.promotion_type_id}
											onChange={updateField('promotion_type_id')}>
											<MenuItem value="">—</MenuItem>
											{form.promotion_type_id
												&& !types.some((t) => String(t.id) === form.promotion_type_id) ? (
													<MenuItem value={form.promotion_type_id} disabled>{form.type_name || `#${form.promotion_type_id}`}</MenuItem>
												) : null}
											{types.map((t) => (
												<MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
											))}
										</TextField>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<TextField
											fullWidth
											required
											variant="outlined"
											size="small"
											type="date"
											label={language.start_date || 'Start Date'}
											InputLabelProps={{ shrink: true }}
											value={form.start_date}
											error={!!fieldError('start_date')}
											helperText={fieldError('start_date')}
											onChange={updateField('start_date')}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={3}>
										<TextField
											fullWidth
											required
											variant="outlined"
											size="small"
											type="date"
											label={language.end_date || 'End Date'}
											InputLabelProps={{ shrink: true }}
											value={form.end_date}
											onChange={updateField('end_date')}
										/>
									</Grid>
								</Grid>
							</div>
						)}
					</div>

					<div className={clsx(classes.sectionCard, classes.s1)} ref={assignSectionRef(2)}>
						<div className={classes.sectionHead}>
							<Typography className={classes.sectionTitle}>{language.skus_in_promotion}</Typography>
						</div>
						<SkuSection form={form} onFormChange={onFormChange} client={client} language={language} errors={errors} />
					</div>

					<div className={clsx(classes.sectionCard, classes.s2)} ref={assignSectionRef(3)}>
						<div className={classes.sectionHead}>
							<Typography className={classes.sectionTitle}>{language.applicable_outlets}</Typography>
						</div>
						<OutletSection form={form} onFormChange={onFormChange} client={client} language={language} errors={errors} />
					</div>

					<div className={clsx(classes.sectionCard, classes.s3)} ref={assignSectionRef(4)}>
						<div className={classes.sectionHead}>
							<Typography className={classes.sectionTitle}>{language.compliance_tasks}</Typography>
						</div>
						<TaskSection form={form} onFormChange={onFormChange} language={language} errors={errors} />
					</div>
				</div>
			</div>

			<Dialog
				open={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				TransitionComponent={GrowTransition}
				maxWidth="xs">
				<DialogTitle>{language.confirmation}</DialogTitle>
				<DialogContent>
					<DialogContentText>{language.unsaved_changes_confirm}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDiscard(false)} color="inherit">
						{language.cancel}
					</Button>
					<Button
						onClick={() => { setConfirmDiscard(false); onClose(false); }}
						color="secondary"
						variant="contained"
						disableElevation>
						{language.discard}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={pendingBrand !== null}
				onClose={() => setPendingBrand(null)}
				TransitionComponent={GrowTransition}
				maxWidth="xs">
				<DialogTitle>{language.confirmation}</DialogTitle>
				<DialogContent>
					<DialogContentText>{language.change_brand_confirm}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingBrand(null)} color="inherit">
						{language.cancel}
					</Button>
					<Button onClick={confirmBrandChange} color="secondary" variant="contained" disableElevation>
						{language.ok || 'OK'}
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
}
